
import { BrowserRouter as Router, Route, Routes, useNavigate } from "react-router-dom";
import { useEffect, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SessionContextProvider } from "@supabase/auth-helpers-react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Home } from "./pages/Home";
import Login from "./pages/Login";
import SignUp from "./pages/SignUp";
import EventDetails from "./pages/EventDetails";
import CreateEvent from "./pages/CreateEvent";
import Profile from "./pages/Profile";
import ReviewEvent from "./pages/ReviewEvent";
import Verify from "./pages/Verify";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { initializeNavigation } from "./services/navigationService";
import { supabase } from "./integrations/supabase/client";
import { Layout } from "./components/Layout";
import DesignSystem from "./pages/DesignSystem";
import RSVPPage from "./features/rsvp/RSVPPage";
import UpdateRSVPPage from "./features/rsvp/UpdateRSVPPage";
import { SentryErrorBoundary } from "./components/error/ErrorBoundary";
import * as Sentry from "@sentry/react";
import { useUser } from "@supabase/auth-helpers-react";
import { setUserContext } from "./integrations/sentry/client";
import { setUserIdentity, trackEvent } from "./integrations/posthog/client";

// Create a Sentry-wrapped router
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const NavigationInitializer = () => {
  const navigate = useNavigate();
  const user = useUser();
  
  // Initialize navigation
  useEffect(() => {
    initializeNavigation(navigate);
  }, [navigate]);
  
  // Set user context for Sentry when user is authenticated
  useEffect(() => {
    if (user) {
      // Set Sentry user context
      setUserContext({
        id: user.id,
        email: user.email
      });
      
      // Set PostHog user identity
      setUserIdentity(user.id, {
        email: user.email,
        provider: user.app_metadata?.provider || 'phone',
        $set: {
          last_login: new Date().toISOString()
        }
      });
      
      // Track login event
      trackEvent('user_authenticated');
    }
  }, [user]);
  
  return null;
};

function App() {
  return (
    <SentryErrorBoundary>
      <SessionContextProvider supabaseClient={supabase}>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Router>
              <NavigationInitializer />
              <SentryRoutes>
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/verify" element={<Verify />} />
                <Route path="/design-system" element={<DesignSystem />} />
                
                <Route path="/" element={
                  <Layout>
                    <ProtectedRoute><Home /></ProtectedRoute>
                  </Layout>
                } />
                <Route path="/home" element={
                  <Layout>
                    <ProtectedRoute><Home /></ProtectedRoute>
                  </Layout>
                } />
                <Route path="/create-event" element={
                  <Layout>
                    <ProtectedRoute><CreateEvent /></ProtectedRoute>
                  </Layout>
                } />
                <Route path="/review-event" element={
                  <Layout>
                    <ProtectedRoute><ReviewEvent /></ProtectedRoute>
                  </Layout>
                } />
                <Route path="/profile" element={
                  <Layout>
                    <ProtectedRoute><Profile /></ProtectedRoute>
                  </Layout>
                } />
                
                <Route path="/events/:eventId" element={
                  <Layout>
                    <ProtectedRoute><EventDetails /></ProtectedRoute>
                  </Layout>
                } />
                
                <Route path="/events/:eventId/rsvp" element={
                  <RSVPPage />
                } />
                
                <Route path="/events/:eventId/update-rsvp" element={
                  <Suspense fallback={<div>Loading...</div>}>
                    <UpdateRSVPPage />
                  </Suspense>
                } />
              </SentryRoutes>
              <Toaster />
            </Router>
          </TooltipProvider>
        </QueryClientProvider>
      </SessionContextProvider>
    </SentryErrorBoundary>
  );
}

export default App;
